import React from 'react';
import {type FieldPath, type FieldValues, useController} from 'react-hook-form';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '@netx/core-components/Form';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {UserGroupItemType} from '../../constants';
import type {UserGroupItem} from '../../types';
import {
	UserGroupAutoList,
	type UserGroupAutoListProps,
} from './UserGroupAutoList';

export {UserGroupItemType} from '../../constants';

export interface ReactHookFormUserGroupAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		UserGroupAutoListProps<Multiple, DisableClearable>,
		TFieldValues,
		TName
	> {
	typeName?: string;
	valueLabelName?: string;
	parseInType?: (type: any) => UserGroupItemType | undefined;
	parseOutType?: (type: UserGroupItemType) => any;
}

const defaultParseOutType = (type?: UserGroupItemType | null) => type;
const defaultParseInType = (type: any) =>
	type === UserGroupItemType.USER
		? UserGroupItemType.USER
		: UserGroupItemType.GROUP;

// TODO: multiple selections needs some work when/if we ever want to use it
// it is a little complicated - I actually really dislike this autolookup
// we have to track ids and types
// so when dealing with multiple values you need to break them up into two arrays
// there is no form object where this can be simple - so the data will always needs some form and translation
export const ReactHookFormUserGroupAutoListPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		typeName,
		valueLabelName,
		parseInType = defaultParseInType,
		parseOutType = defaultParseOutType,
		ControllerProps,
		...props
	}: ReactHookFormUserGroupAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const fieldName = props.name || 'user-group-auto-list';
	typeName = typeName ?? `${fieldName}-type`;
	valueLabelName = valueLabelName ?? `${fieldName}-displayLabel`;

	const typeController = useController<TFieldValues, any>({
		control,
		name: typeName,
		shouldUnregister: control._options.shouldUnregister,
		rules: ControllerProps?.rules,
	});

	const labelController = useController<TFieldValues, any>({
		control,
		name: valueLabelName,
		shouldUnregister: control._options.shouldUnregister,
		rules: ControllerProps?.rules,
	});

	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={fieldName as TName}
			render={({field, formState}) => {
				return (
					<UserGroupAutoList<Multiple, DisableClearable>
						error={formState.errors[fieldName]?.message?.toString()}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={combineRefs(ref, field.ref)}
						name={fieldName}
						// value is comprised of two different "fields"
						value={
							field.value != null
								? props.multiple
									? (field.value as UserGroupItem[]).map((v, idx) => {
											return {
												id: v,
												type: parseInType(typeController.field.value[idx]),
												name: labelController.field.value[idx],
											};
									  })
									: {
											id: field.value,
											type: parseInType(typeController.field.value),
											name: labelController.field.value,
									  }
								: props.multiple
								? []
								: (null as any)
						}
						onChange={splitEventHandler(props.onChange, (event, value) => {
							if (props.multiple) {
								const data = (value as UserGroupItem[]).reduce<{
									ids: UserGroupItem['id'][];
									types: UserGroupItem['type'][];
									labels: UserGroupItem['name'][];
								}>(
									(accum, v) => {
										accum.ids.push(v.id);
										accum.labels.push(v.name);
										accum.types.push(parseOutType(v.type));
										return accum;
									},
									{
										ids: [],
										types: [],
										labels: [],
									},
								);
								typeController.field.onChange(data.types);
								labelController.field.onChange(data.labels);
								// for proper (immediate validation) this field change must come after the auxiliary ones
								field.onChange(data.ids);
							} else {
								typeController.field.onChange(
									parseOutType((value as UserGroupItem)?.type),
								);
								labelController.field.onChange(
									(value as UserGroupItem)?.name ?? '',
								);
								// for proper (immediate validation) this field change must come after the auxiliary ones
								field.onChange((value as UserGroupItem)?.id || null);
							}
						})}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

export const ReactHookFormUserGroupAutoList = React.forwardRef<
	HTMLDivElement,
	ReactHookFormUserGroupAutoListProps
>(ReactHookFormUserGroupAutoListPrimitive) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormUserGroupAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
) => JSX.Element;

export default ReactHookFormUserGroupAutoList;
